// PrintCreditNote.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar
} from "@mui/material";
import { format } from "date-fns";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import AGO_Logo from "../../../Assets/Images/AGO-Logo.png";
import { AccountsandFinanceurl, companyManagementUrl, userManagementUrl } from "../../../config";
import axios from "axios";

const baseURL = process.env.REACT_APP_API_URL || 'https://matrix.mcb.co.ke/usermanagement';

const baseURL2 = companyManagementUrl.uri;

const LOGO_ORIGIN = (() => {
  try {
    return new URL(baseURL2).origin;
  } catch {
    return 'https://matrix.mcb.co.ke';
  }
})();
 

// ---- Logo path normalization (identical logic to PrintInvoice.jsx, ----
// ---- except it resolves against LOGO_ORIGIN instead of baseURL)     ----
const normalizeLogoPath = (rawPath) => {
  if (!rawPath || typeof rawPath !== 'string') return null;
 
  const trimmed = rawPath.trim();
  if (!trimmed) return null;
 
  const isWindowsPath = /^[a-zA-Z]:[\\/]/.test(trimmed);
  if (isWindowsPath) return null;
 
  let cleaned = trimmed.replace(/^(\.\.\/|\.\/)+/, '');
  cleaned = cleaned.replace(/^\/+/, '');
 
  if (!cleaned.startsWith('assets/')) {
    return null;
  }
 
  return `${LOGO_ORIGIN.replace(/\/+$/, '')}/${cleaned}`;
};


// ---- Color extraction helpers ----
const DEFAULT_COLORS = { primary: "#8A3533", secondary: "#9EBB3C" };

const toHex = (r, g, b) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("")}`;

const colorDistance = (a, b) =>
  Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);

const luminance = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const [rl, gl, bl] = [r, g, b].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

const readableTextColor = (hex) => (luminance(hex) > 0.5 ? "#1a1a1a" : "#ffffff");

const extractColorsFromImage = (imgSrc) => {
  return new Promise((resolve) => {
    if (!imgSrc) return resolve(DEFAULT_COLORS);

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const size = 60;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        const counts = {};
        for (let i = 0; i < data.length; i += 4) {
          const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
          if (a < 200) continue;
          const brightness = (r + g + b) / 3;
          if (brightness > 235 || brightness < 20) continue;

          const key = [
            Math.round(r / 16) * 16,
            Math.round(g / 16) * 16,
            Math.round(b / 16) * 16,
          ].join(",");
          counts[key] = (counts[key] || 0) + 1;
        }

        const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
        if (sorted.length === 0) return resolve(DEFAULT_COLORS);

        const primaryRgb = sorted[0][0].split(",").map(Number);
        const primary = toHex(...primaryRgb);

        let secondary = DEFAULT_COLORS.secondary;
        for (let i = 1; i < sorted.length; i++) {
          const rgb = sorted[i][0].split(",").map(Number);
          if (colorDistance(primaryRgb, rgb) > 90) {
            secondary = toHex(...rgb);
            break;
          }
        }

        resolve({ primary, secondary });
      } catch (err) {
        console.error("Color extraction failed:", err);
        resolve(DEFAULT_COLORS);
      }
    };
    img.onerror = () => resolve(DEFAULT_COLORS);
    img.src = imgSrc;
  });
};
// -----------------------------------

const PrintCreditNote = ({ data }) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);
  const userId = parseInt(decodedToken.Id) || null;
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSignature, setUserSignature] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [companyData, setCompanyData] = useState(null);
  const [bankData, setBankData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [brandColors, setBrandColors] = useState(DEFAULT_COLORS);

  console.log("Credit Note Data:", data);

  useEffect(() => {
    const fetchCompanyandBankData = async () => {
      try {
        const companyQuery = `
          {
          allCompanies {
                id
                companyName
                companyEmail
                contactEmail
                contactEmailPassword
                emailDisplayName
                emailClient
                emailClientPortNumber
                subscriptionId
                createdBy
                createdDate
                updatedBy
                updatedDate
                logoPath
                address
                poBox
                phone
                mobile
                website
                pinVat
                tagline
            }
        }
        `;
        const bankQuery = `
          {
  allBanks {
      id
      bankName
      bankSwiftCode
      finCode
      mobile
      createdDate
      createdBy
      email
      companyId
      company
    }
  }
        `;

        const response = await graphqlQuery(
          companyQuery,
          companyManagementUrl.uri,
        );

        if (response && response.length > 0) {
          const userCompanyId = companyId;
          const company = response.find(c => c.id === userCompanyId) 
                        || response[0];
          setCompanyData(company);
          console.log("Company Data1:", response[0]);

          const normalizedLogoUrl = normalizeLogoPath(company.logoPath);
          setLogoUrl(normalizedLogoUrl);
          setLogoLoadFailed(false);

          if (normalizedLogoUrl) {
            const colors = await extractColorsFromImage(normalizedLogoUrl);
            setBrandColors(colors);
          } else {
            setBrandColors(DEFAULT_COLORS);
          }
        }

        const bankResponse = await graphqlQuery(bankQuery, AccountsandFinanceurl.uri);

        if (bankResponse && bankResponse.length > 0) {
          const companyBanks = bankResponse.filter(b => b.companyId === companyId);
          setBankData(companyBanks?.length ? companyBanks[0] : null);
        }
      } catch (err) {
        console.error("Error fetching company and bank data:", err);
      }
    };

    fetchCompanyandBankData();
  }, []);

  console.log("Company Data:", companyData);
  console.log("Bank Data:", bankData);

  useEffect(() => {
    console.log("Company Data updated:", companyData);
    console.log("Bank Data updated:", bankData);
  }, [companyData, bankData]);

  useEffect(() => {
    const fetchLineItems = async () => {
      if (!data || !data[0]?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const creditNoteId = data[0].id;

        const creditNoteLinesQuery = `
          query {
            allCreditNotesLinesByCreditId(creditNotesLines: {creditNoteId: ${creditNoteId}}) {
              id
              creditNoteId
              description
              accountSubCategoryId
              accountSubCategoryName
              amount
              vatId
              vatAmount
              isActive
            }
          }
        `;

        const response = await graphqlQuery(
          creditNoteLinesQuery, 
          AccountsandFinanceurl.uri,
        );

        console.log("Credit Note Line Items Response:", response);

        if (response && response.allCreditNotesLinesByCreditId) {
          setLineItems(response.allCreditNotesLinesByCreditId);
        } else if (response && Array.isArray(response)) {
          setLineItems(response);
        } else {
          setLineItems([]);
        }
      } catch (err) {
        console.error("Error fetching credit note lines:", err);
        setError("Failed to load line items");
        setLineItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLineItems();
  }, [data]);

  const fetchUserSignature = async () => {
    setSignatureLoading(true);
    try {
      console.log("Fetching signature for user:", data[0].approvedBy);

      const signatureQuery = `
        query {
          userSignature(userId: ${data[0].approvedBy})
        }
      `;

      const response = await axios.post(
        userManagementUrl.uri,
        {
          query: signatureQuery
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/"/g, '')}`
          }
        }
      );

      console.log("Signature response:", response.data);

      if (response.data?.data?.userSignature) {
        const signaturePath = response.data.data.userSignature;
        const cleanPath = signaturePath.startsWith('/') ? signaturePath.substring(1) : signaturePath;
        const fullSignatureUrl = `${baseURL}/${cleanPath}`;

        console.log("Signature URL:", fullSignatureUrl);

        setUserSignature(fullSignatureUrl);
        setShowSignature(true);
        setSnackbar({
          open: true,
          message: 'Signature loaded successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'No signature found for user',
          severity: 'warning'
        });
      }
    } catch (err) {
      console.error("Error fetching signature:", err);
      setSnackbar({
        open: true,
        message: 'Failed to load signature',
        severity: 'error'
      });
    } finally {
      setSignatureLoading(false);
    }
  };

  const fetchSignatureAsBase64 = async () => {
    setSignatureLoading(true);
    try {
      console.log("Fetching signature for user:", data[0].approvedBy);

      const signatureQuery = `
        query {
          userSignature(userId: ${data[0].approvedBy})
        }
      `;

      const response = await axios.post(
        userManagementUrl.uri,
        { query: signatureQuery },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/"/g, '')}`
          }
        }
      );

      if (response.data?.data?.userSignature) {
        const signaturePath = response.data.data.userSignature;
        const cleanPath = signaturePath.startsWith('/') ? signaturePath.substring(1) : signaturePath;
        const imageUrl = `${baseURL}/${cleanPath}`;

        console.log("Fetching image from:", imageUrl);

        const imageResponse = await axios.get(imageUrl, {
          responseType: 'arraybuffer',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/"/g, '')}`
          }
        });

        const base64 = btoa(
          new Uint8Array(imageResponse.data).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ''
          )
        );

        const contentType = imageResponse.headers['content-type'] ||
                           (signaturePath.endsWith('.png') ? 'image/png' :
                            signaturePath.endsWith('.jpg') || signaturePath.endsWith('.jpeg') ? 'image/jpeg' :
                            signaturePath.endsWith('.gif') ? 'image/gif' : 'image/png');

        const base64Image = `data:${contentType};base64,${base64}`;

        setUserSignature(base64Image);
        setShowSignature(true);
        setSnackbar({
          open: true,
          message: 'Signature loaded successfully',
          severity: 'success'
        });
      }
    } catch (err) {
      console.error("Error:", err);
      setSnackbar({
        open: true,
        message: 'Failed to load signature',
        severity: 'error'
      });
    } finally {
      setSignatureLoading(false);
    }
  };

  const toggleSignature = () => {
    if (!showSignature) {
      fetchUserSignature();
    } else {
      setShowSignature(false);
      setUserSignature(null);
      setSnackbar({
        open: true,
        message: 'Signature removed',
        severity: 'info'
      });
    }
  };

  const primaryText = readableTextColor(brandColors.primary);
  const secondaryText = readableTextColor(brandColors.secondary);

  const printStyles = `
    @media print {
      body * {
        visibility: hidden;
      }
      .print-section, .print-section * {
        visibility: visible;
      }
      .print-section {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: white;
        padding: 0;
        margin: 0;
      }
      .no-print {
        display: none !important;
      }
      .loading-section {
        display: none !important;
      }
      @page {
        margin: 10mm;
        size: A4 portrait;
      }
    }

    .print-container {
      min-height: 100vh;
      background: white;
      padding: 10px;
    }

    .print-section {
      padding: 15px;
      font-family: 'Times New Roman', Times, serif;
      background: white;
      max-width: 190mm;
      min-height: 277mm;
      margin: 0 auto;
      position: relative;
      border: 1px solid #ddd;
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
      font-size: 12px;
    }

    .loading-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 200px;
      text-align: center;
    }

    .error-section {
      background: #ffe6e6;
      border: 1px solid #ff4444;
      padding: 20px;
      border-radius: 5px;
      text-align: center;
      margin: 20px;
    }

    .ago-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 2px solid ${brandColors.primary};
    }

    .ago-logo-container {
      width: 120px;
      flex-shrink: 0;
    }

    .ago-logo {
      width: 100%;
      height: auto;
      max-height: 60px;
      object-fit: contain;
      display: block;
    }

    .logo-placeholder {
      width: 120px;
      height: 60px;
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid ${brandColors.primary};
      color: ${primaryText};
      font-weight: bold;
      font-size: 12px;
      text-align: center;
      line-height: 1.2;
    }

    .ago-header-center {
      flex: 1;
      text-align: center;
      padding: 0 15px;
    }

    .firm-name {
      font-size: 16px;
      font-weight: bold;
      color: ${brandColors.primary};
      margin-bottom: 5px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .firm-tagline {
      font-size: 10px;
      color: ${brandColors.secondary};
      font-style: italic;
      margin-bottom: 6px;
      font-weight: 500;
    }

    .firm-address {
      font-size: 9px;
      color: #333;
      margin-bottom: 2px;
      line-height: 1.2;
    }

    .document-title {
      font-size: 14px;
      font-weight: bold;
      color: ${brandColors.primary};
      text-transform: uppercase;
      margin-top: 8px;
      padding: 5px 0;
      border-top: 1px solid ${brandColors.secondary};
      border-bottom: 1px solid ${brandColors.secondary};
    }

    .ago-header-right {
      width: 160px;
      flex-shrink: 0;
      padding-left: 12px;
      border-left: 1px solid ${brandColors.primary};
      font-size: 9px;
    }

    .contact-info {
      margin-bottom: 6px;
      line-height: 1.2;
    }

    .contact-label {
      color: ${brandColors.secondary};
      font-weight: 700;
      display: inline-block;
      width: 50px;
    }

    .contact-value {
      color: ${brandColors.primary};
      font-weight: 500;
    }

    .document-details {
      display: flex;
      justify-content: space-between;
      margin-bottom: 15px;
      padding: 8px 10px;
      background: #f8f8f8;
      border: 1px solid #ddd;
      border-radius: 3px;
      font-size: 11px;
      font-weight: 600;
    }

    .doc-detail-item {
      display: flex;
      align-items: center;
    }

    .doc-detail-label {
      color: ${brandColors.primary};
      margin-right: 6px;
      font-weight: 700;
    }

    .doc-detail-value {
      color: #333;
      font-weight: 600;
    }

    .client-section {
      margin: 15px 0;
      padding: 12px;
      background: linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%);
      border: 1px solid ${brandColors.primary};
      border-radius: 4px;
    }

    .client-header {
      font-weight: bold;
      color: ${brandColors.primary};
      margin-bottom: 10px;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .client-details {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      font-size: 11px;
    }

    .client-detail-item {
      margin-bottom: 6px;
      display: flex;
      align-items: flex-start;
    }

    .detail-label {
      font-weight: bold;
      color: ${brandColors.primary};
      min-width: 60px;
      margin-right: 6px;
      font-size: 10px;
    }

    .detail-value {
      color: #333;
      font-weight: 500;
      flex: 1;
      font-size: 10px;
    }

    .ago-table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid ${brandColors.primary};
      margin: 15px 0;
      font-size: 11px;
      background: white;
      color: #333;
    }

    .ago-table th {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText};
      font-weight: bold;
      padding: 8px 6px;
      border: 1px solid ${brandColors.primary};
      text-align: left;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .ago-table td {
      padding: 6px;
      border: 1px solid #ddd;
      vertical-align: top;
      background: white;
      color: #333;
    }

    .ago-table tr:nth-child(even) td {
      background-color: #f9f9f9;
    }

    .amount-column {
      text-align: right;
      font-family: 'Courier New', monospace;
      font-weight: 600;
      color: #333;
    }

    .summary-section {
      margin-top: 15px;
      display: flex;
      justify-content: flex-end;
    }

    .summary-table {
      width: 250px;
      border-collapse: collapse;
      border: 1px solid ${brandColors.primary};
      background: white;
      font-size: 11px;
    }

    .summary-table td {
      padding: 6px 10px;
      border: 1px solid #ddd;
    }

    .summary-label {
      font-weight: bold;
      background: linear-gradient(135deg, #f9f9f9 0%, #e9e9e9 100%);
      color: ${brandColors.primary};
      font-size: 11px;
    }

    .summary-value {
      text-align: right;
      font-family: 'Courier New', monospace;
      font-weight: bold;
      color: #333;
      font-size: 11px;
    }

    .grand-total {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText} !important;
      font-size: 12px;
    }

    .grand-total .summary-value {
      color: ${primaryText} !important;
      font-weight: 800;
    }

    .terms-section {
      margin-top: 20px;
      padding: 12px;
      border: 1px solid ${brandColors.secondary};
      background: linear-gradient(135deg, #f9f9f9 0%, #f5f5f5 100%);
      border-radius: 4px;
    }

    .terms-title {
      font-weight: bold;
      color: ${brandColors.primary};
      margin-bottom: 8px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .terms-list {
      font-size: 10px;
      line-height: 1.4;
      color: #333;
    }

    .terms-list div {
      margin-bottom: 4px;
      padding-left: 12px;
      position: relative;
    }

    .terms-list div:before {
      content: "•";
      color: ${brandColors.secondary};
      font-weight: bold;
      position: absolute;
      left: 0;
    }

    .footer-section {
      margin-top: 25px;
      padding-top: 15px;
      border-top: 2px solid ${brandColors.secondary};
      font-size: 9px;
      color: #666;
    }

    .footer-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 20px;
    }

    .banking-info {
      flex: 1;
    }

    .banking-title {
      font-weight: bold;
      color: ${brandColors.primary};
      margin-bottom: 6px;
      font-size: 10px;
      text-transform: uppercase;
    }

    .banking-details {
      font-size: 9px;
      line-height: 1.3;
    }

    .banking-details div {
      margin-bottom: 2px;
    }

    .signature-area {
      width: 200px;
      text-align: center;
      flex-shrink: 0;
    }

    .signature-image {
      max-width: 150px;
      max-height: 60px;
      object-fit: contain;
      margin-bottom: 5px;
    }

    .signature-line {
      border-top: 1px solid ${brandColors.primary};
      margin-top: ${showSignature ? '10px' : '50px'};
      padding-top: 5px;
      font-size: 10px;
      color: ${brandColors.primary};
      font-weight: 600;
    }

    .signature-note {
      font-size: 8px;
      margin-top: 3px;
      color: #666;
      font-style: italic;
    }

    .print-controls {
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 10000;
      background: white;
      padding: 15px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      border: 2px solid ${brandColors.primary};
      display: flex;
      gap: 10px;
    }

    .print-btn {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText};
      border: none;
      padding: 10px 20px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 12px;
      font-weight: bold;
      transition: all 0.3s ease;
    }

    .print-btn:hover {
      transform: translateY(-1px);
      opacity: 0.88;
    }

    .close-btn {
      background: linear-gradient(135deg, #666 0%, #555 100%);
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 12px;
      font-weight: bold;
      transition: all 0.3s ease;
    }

    .close-btn:hover {
      background: linear-gradient(135deg, #555 0%, #666 100%);
      transform: translateY(-1px);
    }

    .signature-btn {
      background: ${showSignature
        ? 'linear-gradient(135deg, #666 0%, #555 100%)'
        : `linear-gradient(135deg, ${brandColors.secondary} 0%, ${brandColors.secondary} 100%)`};
      color: ${showSignature ? '#ffffff' : secondaryText};
      border: none;
      padding: 10px 20px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 12px;
      font-weight: bold;
      transition: all 0.3s ease;
      min-width: 140px;
    }

    .signature-btn:hover {
      transform: translateY(-1px);
      opacity: 0.9;
    }

    .signature-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const handleImageError = (e) => {
    setLogoLoadFailed(true);
    const img = e.target;
    img.src = AGO_Logo;
  };

  const creditNote = data?.[0] || {};

  const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const vatAmount = lineItems.reduce((sum, item) => sum + (parseFloat(item.vatAmount) || 0), 0);
  const totalAmount = subtotal + vatAmount;

  const companyInfo = {
    name: companyData?.companyName || "set at company management",
    address: companyData?.address || "set at company management",
    poBox: companyData?.poBox || "set at company management",
    phone: companyData?.phone || "set at company management",
    mobile: companyData?.mobile || "set at company management",
    email: companyData?.companyEmail || "set at company management",
    website: companyData?.website || "set at company management",
    pinVat: companyData?.pinVat || "set at company management",
    tagline: companyData?.tagline || "set at company management"
  };

  const bankingInfo = {
    accountName: bankData?.bankName || "set at bank management",
    accountNo: bankData?.finCode || "set at bank management",
    bank: bankData?.bankName || "set at bank management",
    branch: bankData?.branch || "set at bank management",
    swiftCode: bankData?.bankSwiftCode || "set at bank management",
    sortCode: bankData?.finCode || "set at bank management"
  };

  const clientInfo = {
    name: creditNote.clientName || "ABC Manufacturing Company Limited",
    address: creditNote.clientAddress || "P.O Box 12345, Industrial Area",
    contactPerson: creditNote.contactPerson || "Mr. John Smith",
    email: creditNote.clientEmail || "accounts@abcmanufacturing.co.ke",
    phone: creditNote.clientPhone || "+254 700 123 456"
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  if (loading) {
    return (
      <Box className="print-container">
        <style>{printStyles}</style>
        <Box className="loading-section">
          <CircularProgress size={40} style={{ marginBottom: 20 }} />
          <Typography variant="h6">Loading Credit Note Details...</Typography>
          <Typography variant="body2" style={{ marginTop: 10 }}>
            Fetching line items for Credit Note #{creditNote.id}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="print-container">
        <style>{printStyles}</style>
        <Box className="error-section">
          <Typography variant="h6" color="error">Error Loading Credit Note</Typography>
          <Typography variant="body2" style={{ marginTop: 10 }}>
            {error}
          </Typography>
          <button
            className="print-btn"
            onClick={() => window.location.reload()}
            style={{ marginTop: 15 }}
          >
            Retry
          </button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="print-container">
      <style>{printStyles}</style>

      <Box className="print-controls no-print">
        <button
          className="print-btn"
          onClick={handlePrint}
        >
          🖨️ Print
        </button>
        <button
          className="signature-btn"
          onClick={toggleSignature}
          disabled={signatureLoading}
        >
          {signatureLoading ? (
            <CircularProgress size={16} style={{ color: 'inherit', marginRight: 8 }} />
          ) : null}
          {showSignature ? '✖ Manual Sign' : '🖊️ System Signature'}
        </button>
        
      </Box>

      <Box className="print-section">
        <div className="ago-header">
          <div className="ago-logo-container">
            <img
              src={logoUrl && !logoLoadFailed ? logoUrl : AGO_Logo}
              alt={companyInfo.name}
              className="ago-logo"
              onError={handleImageError}
            />
          </div>

          <div className="ago-header-center">
            <div className="firm-name">{companyInfo.name}</div>
            <div className="firm-tagline">{companyInfo.tagline}</div>
            <div className="firm-address">{companyInfo.address}</div>
            <div className="firm-address">{companyInfo.poBox}</div>
            <div className="document-title">CREDIT NOTE</div>
          </div>

          <div className="ago-header-right">
            <div className="contact-info">
              <span className="contact-label">Tel:</span>
              <span className="contact-value">{companyInfo.phone}</span>
            </div>
            <div className="contact-info">
              <span className="contact-label">Mobile:</span>
              <span className="contact-value">{companyInfo.mobile}</span>
            </div>
            <div className="contact-info">
              <span className="contact-label">Email:</span>
              <span className="contact-value">{companyInfo.email}</span>
            </div>
            <div className="contact-info">
              <span className="contact-label">Website:</span>
              <span className="contact-value">{companyInfo.website}</span>
            </div>
            <div className="contact-info">
              <span className="contact-label">PIN/VAT:</span>
              <span className="contact-value">{companyInfo.pinVat}</span>
            </div>
          </div>
        </div>

        <Box className="document-details">
          <div className="doc-detail-item">
            <span className="doc-detail-label">Credit Note No:</span>
            <span className="doc-detail-value">
              {creditNote.documentNumber || `CN-${creditNote.id?.toString().padStart(4, '0')}` || 'CN-2024-001'}
            </span>
          </div>
          <div className="doc-detail-item">
            <span className="doc-detail-label">Date:</span>
            <span className="doc-detail-value">{formatDate(creditNote.issueDate) || format(new Date(), 'dd/MM/yyyy')}</span>
          </div>
          <div className="doc-detail-item">
            <span className="doc-detail-label">Status:</span>
            <span className="doc-detail-value" style={{ 
              color: creditNote.statusId === 3 ? '#4CAF50' : '#FF9800',
              fontWeight: 'bold'
            }}>
              {creditNote.statusName || 'Pending'}
            </span>
          </div>
        </Box>

        <div className="client-section">
          <div className="client-header">CREDIT ISSUED TO:</div>
          <div className="client-details">
            <div className="client-detail-item">
              <span className="detail-label">Client:</span>
              <span className="detail-value">{clientInfo.name}</span>
            </div>
            <div className="client-detail-item">
              <span className="detail-label">Contact:</span>
              <span className="detail-value">{clientInfo.contactPerson}</span>
            </div>
            <div className="client-detail-item">
              <span className="detail-label">Address:</span>
              <span className="detail-value">{clientInfo.address}</span>
            </div>
            <div className="client-detail-item">
              <span className="detail-label">Phone:</span>
              <span className="detail-value">{clientInfo.phone}</span>
            </div>
            <div className="client-detail-item">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{clientInfo.email}</span>
            </div>
            <div className="client-detail-item">
              <span className="detail-label">Case No:</span>
              <span className="detail-value">{creditNote.caseNumber || 'C-2024-001'}</span>
            </div>
            <div className="client-detail-item">
              <span className="detail-label">Case Title:</span>
              <span className="detail-value">{creditNote.caseTitle || 'N/A'}</span>
            </div>
            <div className="client-detail-item">
              <span className="detail-label">Case Description:</span>
              <span className="detail-value">{creditNote.caseDescription || 'N/A'}</span>
            </div>
          </div>
        </div>

        <TableContainer component={Paper} elevation={0}>
          <Table className="ago-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '30px' }}>#</TableCell>
                <TableCell>Description of Credit Items</TableCell>
                <TableCell style={{ width: '80px', textAlign: 'right' }}>Amount (KES)</TableCell>
                <TableCell style={{ width: '80px', textAlign: 'right' }}>VAT (KES)</TableCell>
                <TableCell style={{ width: '90px', textAlign: 'right' }}>Total (KES)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.length > 0 ? (
                lineItems.map((item, index) => (
                  <TableRow key={item.id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.description || "Credit adjustment"}</TableCell>
                    <TableCell className="amount-column">
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell className="amount-column">
                      {formatCurrency(item.vatAmount)}
                    </TableCell>
                    <TableCell className="amount-column" style={{ fontWeight: 'bold' }}>
                      {formatCurrency((parseFloat(item.amount) || 0) + (parseFloat(item.vatAmount) || 0))}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: 'center', color: '#666' }}>
                    No credit items found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className="summary-section">
          <table className="summary-table">
            <tbody>
              <tr>
                <td className="summary-label">Subtotal Credit:</td>
                <td className="summary-value">{formatCurrency(subtotal)}</td>
              </tr>
              <tr>
                <td className="summary-label">VAT Credit:</td>
                <td className="summary-value">{formatCurrency(vatAmount)}</td>
              </tr>
              <tr className="grand-total">
                <td className="summary-label">TOTAL CREDIT:</td>
                <td className="summary-value">{formatCurrency(totalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </Box>

        <div className="terms-section">
          <div className="terms-title">CREDIT NOTE TERMS:</div>
          <div className="terms-list">
            <div>This credit note represents an amount owed to the client.</div>
            <div>The credit can be applied towards future invoices or refunded as per client preference.</div>
            <div>Credits are valid for 12 months from the date of issue.</div>
            <div>Please contact our accounts department to arrange for application or refund of this credit.</div>
            <div>All amounts are stated in Kenya Shillings (KES).</div>
          </div>
        </div>

        <div className="footer-section">
          <div className="footer-content">
            <div className="banking-info">
              <div className="banking-title">FOR REFUND REQUESTS:</div>
              <div className="banking-details">
                <div><strong>Account Name:</strong> {bankingInfo.accountName}</div>
                <div><strong>Account No:</strong> {bankingInfo.accountNo}</div>
                <div><strong>Bank:</strong> {bankingInfo.bank} - {bankingInfo.branch}</div>
                <div><strong>Swift Code:</strong> {bankingInfo.swiftCode}</div>
                <div style={{ marginTop: '8px', fontStyle: 'italic', color: '${brandColors.primary}' }}>
                  Please provide your banking details for refund processing
                </div>
              </div>
            </div>

            <div className="signature-area">
              {showSignature && userSignature && (
                <img
                  src={userSignature}
                  alt="Signature"
                  className="signature-image"
                  onError={(e) => {
                    console.error("Failed to load signature image:", e.target.src);
                    e.target.style.display = 'none';

                    const parent = e.target.parentElement;
                    if (parent) {
                      const existingError = parent.querySelector('.signature-error');
                      if (existingError) existingError.remove();

                      const errorMsg = document.createElement('div');
                      errorMsg.className = 'signature-error';
                      errorMsg.style.color = '#ff4444';
                      errorMsg.style.fontSize = '10px';
                      errorMsg.style.padding = '5px';
                      errorMsg.style.backgroundColor = '#ffeeee';
                      errorMsg.style.borderRadius = '3px';
                      errorMsg.style.marginBottom = '5px';
                      errorMsg.innerHTML = '⚠️ Signature failed to load';
                      parent.insertBefore(errorMsg, parent.firstChild);
                    }
                  }}
                  onLoad={(e) => {
                    console.log("Signature loaded successfully");
                    const parent = e.target.parentElement;
                    if (parent) {
                      const errorMsg = parent.querySelector('.signature-error');
                      if (errorMsg) errorMsg.remove();
                    }
                  }}
                />
              )}
              <div className="signature-line">
                For {companyInfo.name}
              </div>
              <div className="signature-note">
                {showSignature ? 'Digitally Signed' : 'Authorized Signatory'}
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '15px', fontSize: '8px', color: '#999', fontStyle: 'italic' }}>
            This is a computer generated credit note. {showSignature ? 'Digitally signed' : 'No signature is required'}. Printed on: {format(new Date(), 'dd/MM/yyyy HH:mm')}
          </div>
        </div>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PrintCreditNote;
